import { createSlice, createSelector } from '@reduxjs/toolkit';
import { attendanceApi } from './attendanceApi';
import { RootState } from '../../app/store';

interface AttendanceState {
    isClockedIn: boolean;
    todaySummary: any | null;
    slots: any[];
    lastEventTime: string | null;
    loading: boolean;
    /** Informational only — clocking in is never blocked on a Week Off/Half Day; working it just counts as overtime. */
    dayKind: 'week_off' | 'half_day' | 'working';
}

const initialState: AttendanceState = {
    isClockedIn: false,
    todaySummary: null,
    slots: [],
    lastEventTime: null,
    loading: false,
    dayKind: 'working',
};

/**
 * Attendance Redux Slice
 * Acts as the Single Source of Truth for Global Attendance Synchronization.
 */
const attendanceSlice = createSlice({
    name: 'attendance',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        // Optional: Manual optimistic toggle if needed
        optimisticToggle: (state) => {
            state.isClockedIn = !state.isClockedIn;
        }
    },
    extraReducers: (builder) => {
        // Handle Today Sync
        builder.addMatcher(
            attendanceApi.endpoints.getTodayStatus.matchFulfilled,
            (state, { payload }) => {
                state.isClockedIn = payload.data.isClockedIn;
                state.todaySummary = payload.data.summary;
                state.slots = payload.data.slots || [];
                state.dayKind = payload.data.dayKind || 'working';
                state.loading = false;
            }
        );
        // Handle Toggle Success. The toggle response is just the single record
        // for the slot that was just acted on, not the full {slots: [...]}
        // shape - toggleAttendance invalidates the 'Attendance' tag, which
        // getTodayStatus provides, so a fresh slots[] follows right behind via
        // RTK Query's automatic refetch (the button's isLoading covers that gap).
        builder.addMatcher(
            attendanceApi.endpoints.toggleAttendance.matchFulfilled,
            (state, { payload }) => {
                state.isClockedIn = payload.success ? !!(payload.data.first_in && !payload.data.last_out) : state.isClockedIn;
                state.todaySummary = payload.data;
                state.lastEventTime = new Date().toISOString();
                state.loading = false;
            }
        );
    },
});

export const { setLoading, optimisticToggle } = attendanceSlice.actions;
export default attendanceSlice.reducer;

/**
 * SELECTORS
 */
const selectAttendanceState = (state: RootState) => state.attendance;

export const selectIsClockedIn = createSelector(
    [selectAttendanceState],
    (attendance) => attendance.isClockedIn
);

export const selectTodaySummary = createSelector(
    [selectAttendanceState],
    (attendance) => attendance.todaySummary
);

/**
 * Two-shift-per-day model: `slots` describes today's (up to two) rostered
 * shifts and each one's clock state, mirroring what ESS-Mobile's clock-in
 * screen already reads from the same backend payload (attendanceService.
 * getTodayAttendance). Used to label the web Clock In/Out button with which
 * shift it's about to act on, instead of a generic, shift-unaware button.
 */
export const selectSlots = createSelector(
    [selectAttendanceState],
    (attendance) => attendance.slots
);

export const selectActiveSlot = createSelector(
    [selectSlots],
    (slots) => slots.find((s: any) => s.inProgress) ?? null
);

export const selectNextSlot = createSelector(
    [selectSlots],
    (slots) => slots.find((s: any) => !s.completed && !s.inProgress) ?? null
);

export const selectAllShiftsDone = createSelector(
    [selectSlots],
    (slots) => slots.length > 0 && slots.every((s: any) => s.completed)
);

export const selectDayKind = createSelector(
    [selectAttendanceState],
    (attendance) => attendance.dayKind
);

export const selectIsWeekOff = createSelector([selectDayKind], (dayKind) => dayKind === 'week_off');
export const selectIsHalfDay = createSelector([selectDayKind], (dayKind) => dayKind === 'half_day');

/**
 * Task 3: Memoized Selector for Professional Formatting (Pillar 4)
 * Transforms raw minutes into "Hh Mm" format.
 */
export const selectFormattedTotalHours = createSelector(
    [selectTodaySummary],
    (summary) => {
        if (!summary || !summary.total_work_hours) return '0h 0m';
        
        const totalMinutes = parseInt(summary.total_work_hours, 10);
        if (isNaN(totalMinutes)) return '0h 0m';

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        
        return `${hours}h ${minutes}m`;
    }
);
